import Vue from "vue";
import VueRouter from "vue-router";
import { APP_ACTIONS } from '@/store/modules/app';
import store from "@/store";
import * as Sentry from "@sentry/vue";
import routes from "./routes";

Vue.use(VueRouter);

const router = new VueRouter({
  mode: 'history',
  base: process.env.PUBLIC_PATH,
//  base: '/clients/of/',
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

// Before each route evaluates
// This callback runs before every route change, including on page load.
// eslint-disable-next-line
router.beforeEach((to, from, next) => {
  // This goes through the matched routes from last to first, finding the closest route with a title.
  // e.g., if we have `/some/deep/nested/route` and `/some`, `/deep`, and `/nested` have titles,
  // `/nested`'s will be chosen.
  const nearestWithTitle = to.matched.slice().reverse().find(r => r.meta && r.meta.title);

  // Find the nearest route element with meta and link tags.
  const nearestWithMeta = to.matched.slice().reverse().find(r => r.meta && r.meta.metaTags);
  const nearestWithLink = to.matched.slice().reverse().find(r => r.meta && r.meta.linkTags);

  const previousNearestWithMeta = from.matched.slice().reverse().find(r => r.meta && r.meta.metaTags);
  const previousNearestWithLink = from.matched.slice().reverse().find(r => r.meta && r.meta.linkTags);

  // If a route with a title was found, set the document (page) title to that value.
  if(nearestWithTitle) {
    document.title = nearestWithTitle.meta.title;
  } else if(previousNearestWithMeta) {
    document.title = previousNearestWithMeta.meta.title;
  }

  // Remove any stale meta tags from the document using the key attribute we set below.
  Array.from(document.querySelectorAll('[data-vue-router-controlled]')).map(el => el.parentNode.removeChild(el));

  // Skip rendering meta tags if there are none.
  if(!nearestWithMeta) return next();
  if(!nearestWithLink) return next();

  // Turn the meta tag definitions into actual elements in the head.
  nearestWithMeta.meta.metaTags.map(tagDef => {
    const tag = document.createElement('meta');

    Object.keys(tagDef).forEach(key => {
      tag.setAttribute(key, tagDef[key]);
    });

    // We use this to track which meta tags we create so we don't interfere with other ones.
    tag.setAttribute('data-vue-router-controlled', '');

    return tag;
  })
  // Add the meta tags to the document head.
    .forEach(tag => document.head.appendChild(tag));


  // Turn the meta LINK definitions into actual elements in the head.
  nearestWithLink.meta.linkTags.map(tagDef => {
    const tag = document.createElement('link');

    Object.keys(tagDef).forEach(key => {
      tag.setAttribute(key, tagDef[key]);
    });

    // We use this to track which meta tags we create so we don't interfere with other ones.
    tag.setAttribute('data-vue-router-controlled', '');

    return tag;
  })
  // Add the meta tags to the document head.
    .forEach(tag => document.head.appendChild(tag));

  next();
});

// Create a `beforeResolve` hook, which fires whenever
// `beforeRouteEnter` and `beforeRouteUpdate` would. This
// allows us to ensure data is fetched even when params change,
// but the resolved route does not. We put it in `meta` to
// indicate that it's a hook we created, rather than part of
// Vue Router
router.beforeResolve(async (to, from, next) => {
  try {
    // For each matched route...
    // eslint-disable-next-line no-restricted-syntax
    for (const route of to.matched) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(to, from, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              if (from.name === args[0].name) {
                // Complete the animation of the route progress bar.
                store.dispatch(`app/${APP_ACTIONS.SET_PRELOADING}`, false);
              }
              // Complete the redirect.
              next(...args);
              reject(new Error("Redirected"));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    Sentry.captureException(error);
    return;
  }

  // If we reach this point, continue resolving the route.
  next();
});

// When each route is finished evaluating...
router.afterEach(async () => {
  // Complete the animation
  await store.dispatch(`app/${APP_ACTIONS.SET_PRELOADING}`, false);
});

export { router };
